import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Swal from "sweetalert2";

import AuthService from "../../../../services/Admin/AuthService";
import MessagingService from "../../../../services/Admin/MessagingService";
import HttpError from "../../../../utill/HttpError";
import LoadingContext from "../../../../store/loading-context";

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */
const DEFAULT_CONFIG = {
  position: "center",
  background: { type: "gradient", color: "#E0F4F7", color2: "#B8E6EE" },
  title: {
    size: 28,
    bold: true,
    italic: false,
    align: "center",
    color: "#1F2D3D",
  },
  body: {
    size: 16,
    bold: false,
    italic: false,
    align: "center",
    color: "#5B6572",
  },
  image: { url: "", widthPct: 100, height: null, radius: 12, fit: "fillWidth" },
  button: {
    show: true,
    text: "OK",
    color: "#17A2B8",
    textColor: "#FFFFFF",
    textSize: 17,
    radius: 10,
  },
  close: { show: true, color: "#1F2D3D" },
  action: { type: "none", value: "" },
  backdrop: { color: "rgba(0,0,0,0.55)", dismissible: true },
  card: { radius: 20, maxWidth: 380 },
};

const EMPTY_FORM = {
  id: null,
  name: "",
  title: "",
  body: "",
  status: "inactive",
  frequency: "every_open",
  config: DEFAULT_CONFIG,
  startsAt: null,
  endsAt: null,
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const safe = (v) => (v == null ? "" : String(v));

/* parse a config that may be an object or a JSON string */
const parseConfig = (c) => {
  if (!c) return {};
  if (typeof c === "object") return c;
  if (typeof c === "string") {
    try {
      return JSON.parse(c);
    } catch (_) {
      return {};
    }
  }
  return {};
};

/* get the image url from a list item, regardless of shape */
const getItemImageUrl = (item) => {
  const cfg = parseConfig(item?.config);
  return cfg?.image?.url || "";
};

const mergeConfig = (base, incoming) => {
  const b = base || {};
  const i = incoming || {};
  const next = { ...b, ...i };
  const sections = [
    "background",
    "title",
    "body",
    "image",
    "button",
    "close",
    "action",
    "backdrop",
    "card",
  ];
  for (const key of sections) {
    if (b[key] !== undefined || i[key] !== undefined) {
      next[key] = { ...(b[key] || {}), ...(i[key] || {}) };
    }
  }
  return next;
};

const setNested = (obj, path, value) => {
  const keys = path.split(".");
  const walk = (node, i) => {
    if (i === keys.length - 1) return { ...node, [keys[i]]: value };
    const key = keys[i];
    return { ...node, [key]: walk(node[key] || {}, i + 1) };
  };
  return walk(obj || {}, 0);
};

const formatWhen = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "—";
  const diff = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return then.toLocaleDateString();
};

const FREQ_LABELS = {
  every_open: "Every open",
  once_in_a_day: "Once a day",
  once: "Once ever",
};

/* ------------------------------------------------------------------ */
/*  Small components                                                   */
/* ------------------------------------------------------------------ */

/* Controlled select with fallback — prevents React's select desync */
const Select = ({ value, onChange, children, className = "" }) => {
  const first = React.Children.toArray(children)[0];
  const fallback = first && first.props ? first.props.value : "";
  return (
    <select
      className={"form-control form-control-sm " + className}
      value={value == null || value === "" ? fallback : value}
      onChange={onChange}
    >
      {children}
    </select>
  );
};

/* Modern status pill — dot + label + sliding switch */
const StatusPill = ({ active, onChange, busy }) => (
  <button
    type="button"
    className={"iam-status-pill" + (active ? " is-active" : "")}
    onClick={(e) => {
      e.stopPropagation();
      if (!busy) onChange();
    }}
    disabled={busy}
    title={active ? "Click to deactivate" : "Click to activate"}
  >
    <span className="iam-status-pill__dot" />
    <span className="iam-status-pill__label">
      {active ? "Active" : "Inactive"}
    </span>
    <span className="iam-status-pill__switch">
      <span className="iam-status-pill__knob" />
    </span>
  </button>
);

/* Row thumbnail — shows image or an empty placeholder */
const RowThumb = ({ url }) => {
  const [errored, setErrored] = useState(false);

  if (!url || errored) {
    return (
      <div className="iam-card-row__thumb iam-card-row__thumb--empty">
        <i className="far fa-image" />
      </div>
    );
  }

  return (
    <div className="iam-card-row__thumb">
      <img src={url} alt="" loading="lazy" onError={() => setErrored(true)} />
    </div>
  );
};

const Section = ({ title, right, collapsed, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen || !collapsed);
  return (
    <div className="iam-sec">
      <div className="iam-sec__head">
        <button
          type="button"
          className="iam-sec__toggle"
          onClick={() => setOpen((o) => !o)}
        >
          <i className={`fas fa-chevron-${open ? "down" : "right"} mr-2`} />
          {title}
        </button>
        <div>{right}</div>
      </div>
      {open && <div className="iam-sec__body">{children}</div>}
    </div>
  );
};

const Field = ({ label, span, children }) => (
  <div
    className="iam-field"
    style={span ? { gridColumn: `span ${span}` } : undefined}
  >
    <label>{label}</label>
    {children}
  </div>
);

const ColorField = ({ label, value, onChange }) => (
  <div className="iam-field">
    <label>{label}</label>
    <div className="iam-color">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
      />
      <input
        type="text"
        className="form-control form-control-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={9}
      />
    </div>
  </div>
);

const ToggleBtn = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    className={"iam-toggle" + (active ? " is-active" : "")}
    onClick={onClick}
  >
    <i className={icon} /> {label}
  </button>
);

/* ------------------------------------------------------------------ */
/*  Preview inside phone                                               */
/* ------------------------------------------------------------------ */
const Preview = ({ form }) => {
  const c = form.config || {};
  const bg1 = c.background?.color || "#E0F4F7";
  const bg2 = c.background?.color2 || "#B8E6EE";
  const bgStyle =
    c.background?.type === "solid"
      ? { background: bg1 }
      : { background: `linear-gradient(180deg, ${bg1}, ${bg2})` };

  const titleStyle = {
    fontSize: `${c.title?.size || 28}px`,
    fontWeight: c.title?.bold ? 700 : 400,
    fontStyle: c.title?.italic ? "italic" : "normal",
    textAlign: c.title?.align || "center",
    color: c.title?.color || "#1F2D3D",
    lineHeight: 1.2,
  };
  const bodyStyle = {
    fontSize: `${c.body?.size || 16}px`,
    fontWeight: c.body?.bold ? 700 : 400,
    fontStyle: c.body?.italic ? "italic" : "normal",
    textAlign: c.body?.align || "center",
    color: c.body?.color || "#5B6572",
    lineHeight: 1.45,
  };
  const btnStyle = {
    background: c.button?.color || "#17A2B8",
    color: c.button?.textColor || "#fff",
    borderRadius: `${c.button?.radius || 10}px`,
    fontSize: `${c.button?.textSize || 17}px`,
  };
  const cardStyle = {
    ...bgStyle,
    borderRadius: `${c.card?.radius || 20}px`,
    maxWidth: `${c.card?.maxWidth || 380}px`,
  };
  const pos = c.position || "center";

  const card = (
    <div className="iam-preview__card" style={cardStyle}>
      {c.close?.show && (
        <i
          className="fas fa-times iam-preview__close"
          style={{ color: c.close?.color || "#1F2D3D" }}
        />
      )}
      {form.title && (
        <div style={titleStyle} className="iam-preview__title">
          {form.title}
        </div>
      )}
      {form.body && (
        <div style={bodyStyle} className="iam-preview__body">
          {form.body}
        </div>
      )}
      {c.image?.url && (
        <div
          className="iam-preview__image"
          style={{
            width: `${c.image.widthPct || 100}%`,
            margin: "16px auto 0",
          }}
        >
          <img
            src={c.image.url}
            alt=""
            style={{ borderRadius: `${c.image.radius || 12}px` }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}
      {c.button?.show && (
        <div style={btnStyle} className="iam-preview__button">
          {c.button.text || "OK"}
        </div>
      )}
    </div>
  );

  return (
    <div className="iam-preview">
      <div className="iam-preview__backdrop" />
      <div className={`iam-preview__pos iam-preview__pos--${pos}`}>{card}</div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
const InAppMessage = () => {
  const loadingCtx = useContext(LoadingContext);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    config: DEFAULT_CONFIG,
  }));

  const busyRef = useRef(false);

  /* ---------- load ---------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = AuthService.getAccessToken();
      const json = await MessagingService.getInAppMessages(token);
      if (json && json.status === "SUCCESS" && json.data?.items) {
        setItems(json.data.items);
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- filter ---------- */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const n = safe(i.name).toLowerCase();
      const t = safe(i.title).toLowerCase();
      const b = safe(i.body).toLowerCase();
      return n.includes(q) || t.includes(q) || b.includes(q);
    });
  }, [items, search]);

  /* ---------- form helpers ---------- */
  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setCfg = useCallback((path, value) => {
    setForm((prev) => ({
      ...prev,
      config: setNested(prev.config, path, value),
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm({ ...EMPTY_FORM, config: DEFAULT_CONFIG });
  }, []);

  const openNew = useCallback(() => {
    resetForm();
    setModalOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((item) => {
    setForm({
      id: item.id,
      name: item.name,
      title: item.title,
      body: item.body,
      status: item.status,
      frequency: item.frequency,
      config: mergeConfig(DEFAULT_CONFIG, parseConfig(item.config)),
      startsAt: item.startsAt,
      endsAt: item.endsAt,
    });
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (saving) return;
    setModalOpen(false);
    setPanelOpen(false);
  }, [saving]);

  /* ---------- escape key ---------- */
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  /* ---------- save ---------- */
  const save = useCallback(async () => {
    if (busyRef.current) return;
    if (!form.name.trim())
      return Swal.fire("Opps!", "Name is required.", "warning");
    if (!form.title.trim())
      return Swal.fire("Opps!", "Title is required.", "warning");
    if (!form.body.trim())
      return Swal.fire("Opps!", "Body is required.", "warning");

    busyRef.current = true;
    loadingCtx.startLoading();
    setSaving(true);

    try {
      const token = AuthService.getAccessToken();
      const payload = {
        name: form.name.trim(),
        title: form.title.trim(),
        body: form.body.trim(),
        status: form.status,
        frequency: form.frequency,
        config: form.config,
        startsAt: form.startsAt || null,
        endsAt: form.endsAt || null,
      };

      const res = form.id
        ? await MessagingService.updateInAppMessage(token, form.id, payload)
        : await MessagingService.createInAppMessage(token, payload);

      if (res && res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1200,
          showConfirmButton: false,
        });
        setModalOpen(false);
        resetForm();
        await load();
      } else {
        Swal.fire("Opps!", res?.message || "Save failed.", "error");
      }
    } catch (e) {
      Swal.fire("Opps!", HttpError.getError(e), "error");
    } finally {
      busyRef.current = false;
      loadingCtx.stopLoading();
      setSaving(false);
    }
  }, [form, loadingCtx, resetForm, load]);

  /* ---------- delete ---------- */
  const remove = useCallback(async (id) => {
    if (busyRef.current) return;
    const ok = await Swal.fire({
      title: "Delete this message?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#ef4444",
    });
    if (!ok.isConfirmed) return;

    busyRef.current = true;
    try {
      const token = AuthService.getAccessToken();
      const res = await MessagingService.deleteInAppMessage(token, id);
      if (res && res.status === "SUCCESS") {
        setItems((prev) => prev.filter((i) => i.id !== id));
        Swal.fire({
          icon: "success",
          title: "Deleted",
          timer: 900,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Opps!", res?.message || "Delete failed.", "error");
      }
    } catch (e) {
      Swal.fire("Opps!", HttpError.getError(e), "error");
    } finally {
      busyRef.current = false;
    }
  }, []);

  /* ---------- toggle ---------- */
  const toggleStatus = useCallback(async (item) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      const token = AuthService.getAccessToken();
      const res = await MessagingService.updateInAppMessage(token, item.id, {
        status: newStatus,
      });
      if (res && res.status === "SUCCESS") {
        setItems((prev) =>
          prev.map((i) => (i.id === item.id ? { ...i, status: newStatus } : i)),
        );
        if (newStatus === "active") {
          MessagingService.notifyInAppMessage(token).catch(() => {});
        }
      } else {
        Swal.fire("Opps!", res?.message || "Toggle failed.", "error");
      }
    } catch (e) {
      Swal.fire("Opps!", HttpError.getError(e), "error");
    } finally {
      busyRef.current = false;
    }
  }, []);

  /* ---------- notify apps ---------- */
  const notifyApps = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const token = AuthService.getAccessToken();
      const res = await MessagingService.notifyInAppMessage(token);
      if (res && res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Signal sent",
          text: "Apps will refresh within a few seconds.",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Opps!", res?.message || "Notify failed.", "error");
      }
    } catch (e) {
      Swal.fire("Opps!", HttpError.getError(e), "error");
    } finally {
      busyRef.current = false;
    }
  }, []);

  const activeCount = items.filter((i) => i.status === "active").length;

  /* ------------------------------------------------------------------ */
  /*  Row renderer — used by both the inline list and the drawer        */
  /* ------------------------------------------------------------------ */
  const renderRow = (it, options = {}) => {
    const isEditing = form.id === it.id && modalOpen;
    const thumbUrl = getItemImageUrl(it);
    const showEdit = options.showEdit !== false;

    return (
      <li
        key={it.id}
        className={"iam-card-row" + (isEditing ? " iam-card-row--active" : "")}
        onClick={() => {
          openEdit(it);
          if (options.closeDrawerOnClick) setPanelOpen(false);
        }}
      >
        <div className="iam-card-row__head">
          <StatusPill
            active={it.status === "active"}
            onChange={() => toggleStatus(it)}
          />
          <div className="iam-card-row__meta">
            <span>{safe(it.name) || "—"}</span>
            <span className="iam-dot-sep">·</span>
            <span>{FREQ_LABELS[it.frequency] || it.frequency}</span>
            <span className="iam-dot-sep">·</span>
            <span>{formatWhen(it.at)}</span>
          </div>
          <div className="iam-card-row__actions">
            {showEdit && (
              <button
                type="button"
                className="iam-icon-btn"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(it);
                }}
              >
                <i className="fas fa-pen" />
              </button>
            )}
            <button
              type="button"
              className="iam-icon-btn iam-icon-btn--danger"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                remove(it.id);
              }}
            >
              <i className="far fa-trash-alt" />
            </button>
          </div>
        </div>

        <div className="iam-card-row__main">
          <RowThumb url={thumbUrl} />
          <div className="iam-card-row__text">
            <div className="iam-card-row__title">
              {safe(it.title) || "(untitled)"}
            </div>
            {safe(it.body) && (
              <div className="iam-card-row__body">{safe(it.body)}</div>
            )}
          </div>
        </div>
      </li>
    );
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <React.Fragment>
      <style>{styles}</style>

      {/* =============== TOP BAR =============== */}
      <div className="iam-topbar">
        <div className="iam-topbar__info">
          <h4 className="iam-topbar__title">
            <i className="fas fa-comment-dots mr-2" />
            In-App Messages
          </h4>
          <div className="iam-topbar__stats">
            <span className="iam-stat">
              <strong>{items.length}</strong> total
            </span>
            <span className="iam-stat iam-stat--active">
              <strong>{activeCount}</strong> active
            </span>
          </div>
        </div>

        <div className="iam-topbar__actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={notifyApps}
            title="Ask all running apps to refresh"
          >
            <i className="fas fa-bullhorn mr-1" />
            Notify apps
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPanelOpen(true)}
          >
            <i className="fas fa-list mr-1" />
            All messages
            <span className="iam-badge">{items.length}</span>
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={openNew}
          >
            <i className="fas fa-plus mr-1" />
            New message
          </button>
        </div>
      </div>

      {/* =============== MAIN GRID =============== */}
      <div className="iam-main">
        {/* left — inline list */}
        <div className="iam-main__list">
          <div className="iam-panel">
            <div className="iam-panel__head">
              <span className="iam-panel__title">
                <i className="fas fa-stream mr-1" /> Recent
              </span>
              <button
                type="button"
                className="iam-panel__link"
                onClick={() => setPanelOpen(true)}
              >
                View all <i className="fas fa-arrow-right ml-1" />
              </button>
            </div>

            <div className="iam-panel__search">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Search name, title, or body…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="iam-panel__search-clear"
                  onClick={() => setSearch("")}
                  title="Clear"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            {loading ? (
              <div className="iam-panel__empty">
                <i className="fas fa-spinner fa-spin d-block mb-2" />
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="iam-panel__empty">
                <i className="fas fa-inbox d-block mb-2" />
                {search ? "No messages match your search" : "No messages yet"}
                {!search && (
                  <button
                    type="button"
                    className="iam-empty-cta"
                    onClick={openNew}
                  >
                    Create one
                  </button>
                )}
              </div>
            ) : (
              <ul className="iam-cards">
                {filtered.slice(0, 20).map((it) => renderRow(it))}
              </ul>
            )}
          </div>
        </div>

        {/* right — live preview */}
        <div className="iam-main__preview">
          <div className="iam-preview-card">
            <div className="iam-preview-card__label">Live preview</div>
            <div className="iam-phone">
              <div className="iam-phone__notch" />
              <div className="iam-phone__screen">
                <div className="iam-phone__statusbar">
                  <span>9:41</span>
                  <span className="iam-phone__statusbar-icons">
                    <i className="fas fa-signal" />
                    <i className="fas fa-wifi" />
                    <i className="fas fa-battery-three-quarters" />
                  </span>
                </div>
                <Preview form={modalOpen ? form : EMPTY_FORM} />
              </div>
            </div>
            <div className="iam-preview-card__hint">
              Click a message to edit, or create a new one
            </div>
          </div>
        </div>
      </div>

      {/* =============== MODAL =============== */}
      {modalOpen && (
        <div className="iam-modal-backdrop" onClick={closeModal}>
          <div className="iam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="iam-modal__head">
              <div>
                <div className="iam-modal__title">
                  {form.id ? "Edit in-app message" : "New in-app message"}
                </div>
                <div className="iam-modal__sub">
                  Configure appearance and behaviour
                </div>
              </div>
              <button
                type="button"
                className="iam-modal__close"
                onClick={closeModal}
                disabled={saving}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="iam-modal__body">
              {/* form */}
              <div className="iam-modal__form">
                <Section title="Basics" defaultOpen>
                  <div className="iam-grid-2">
                    <Field label="Name (internal)">
                      <input
                        className="form-control form-control-sm"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="e.g. Welcome promo"
                        maxLength={120}
                      />
                    </Field>
                    <Field label="Status">
                      <Select
                        value={form.status}
                        onChange={(e) => setField("status", e.target.value)}
                      >
                        <option value="inactive">Inactive</option>
                        <option value="active">Active</option>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Frequency">
                    <Select
                      value={form.frequency}
                      onChange={(e) => setField("frequency", e.target.value)}
                    >
                      <option value="every_open">Every open</option>
                      <option value="once_in_a_day">Once a day</option>
                      <option value="once">Once ever</option>
                    </Select>
                  </Field>
                  <Field label="Title">
                    <input
                      className="form-control form-control-sm"
                      value={form.title}
                      onChange={(e) => setField("title", e.target.value)}
                      placeholder="Welcome Aboard!"
                      maxLength={255}
                    />
                  </Field>
                  <Field label="Body">
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      value={form.body}
                      onChange={(e) => setField("body", e.target.value)}
                      placeholder="We're excited to have you…"
                      maxLength={4000}
                    />
                  </Field>
                </Section>

                <Section title="Position" defaultOpen>
                  <div className="iam-chips">
                    {[
                      ["center", "Center", "fas fa-square"],
                      ["top", "Top", "fas fa-arrow-up"],
                      ["bottom", "Bottom", "fas fa-arrow-down"],
                      ["banner_top", "Banner top", "fas fa-window-maximize"],
                      [
                        "banner_bottom",
                        "Banner bottom",
                        "fas fa-window-minimize",
                      ],
                      ["fullscreen", "Fullscreen", "fas fa-expand"],
                    ].map(([v, l, icon]) => (
                      <button
                        key={v}
                        type="button"
                        className={
                          "iam-chip" +
                          (form.config.position === v ? " is-active" : "")
                        }
                        onClick={() => setCfg("position", v)}
                      >
                        <i className={icon + " mr-1"} />
                        {l}
                      </button>
                    ))}
                  </div>
                </Section>

                <Section title="Background">
                  <div className="iam-grid-3">
                    <Field label="Type">
                      <Select
                        value={form.config.background.type}
                        onChange={(e) =>
                          setCfg("background.type", e.target.value)
                        }
                      >
                        <option value="gradient">Gradient</option>
                        <option value="solid">Solid</option>
                      </Select>
                    </Field>
                    <ColorField
                      label="Color 1"
                      value={form.config.background.color}
                      onChange={(v) => setCfg("background.color", v)}
                    />
                    {form.config.background.type === "gradient" && (
                      <ColorField
                        label="Color 2"
                        value={form.config.background.color2}
                        onChange={(v) => setCfg("background.color2", v)}
                      />
                    )}
                  </div>
                </Section>

                <Section title="Title style">
                  <div className="iam-grid-3">
                    <Field label={`Size · ${form.config.title.size}px`}>
                      <input
                        type="range"
                        min={8}
                        max={60}
                        value={form.config.title.size}
                        onChange={(e) =>
                          setCfg("title.size", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                    <ColorField
                      label="Color"
                      value={form.config.title.color}
                      onChange={(v) => setCfg("title.color", v)}
                    />
                    <Field label="Align">
                      <Select
                        value={form.config.title.align}
                        onChange={(e) => setCfg("title.align", e.target.value)}
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </Select>
                    </Field>
                  </div>
                  <div className="iam-toggles">
                    <ToggleBtn
                      active={form.config.title.bold}
                      onClick={() =>
                        setCfg("title.bold", !form.config.title.bold)
                      }
                      icon="fas fa-bold"
                      label="Bold"
                    />
                    <ToggleBtn
                      active={form.config.title.italic}
                      onClick={() =>
                        setCfg("title.italic", !form.config.title.italic)
                      }
                      icon="fas fa-italic"
                      label="Italic"
                    />
                  </div>
                </Section>

                <Section title="Body style">
                  <div className="iam-grid-3">
                    <Field label={`Size · ${form.config.body.size}px`}>
                      <input
                        type="range"
                        min={8}
                        max={40}
                        value={form.config.body.size}
                        onChange={(e) =>
                          setCfg("body.size", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                    <ColorField
                      label="Color"
                      value={form.config.body.color}
                      onChange={(v) => setCfg("body.color", v)}
                    />
                    <Field label="Align">
                      <Select
                        value={form.config.body.align}
                        onChange={(e) => setCfg("body.align", e.target.value)}
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </Select>
                    </Field>
                  </div>
                  <div className="iam-toggles">
                    <ToggleBtn
                      active={form.config.body.bold}
                      onClick={() =>
                        setCfg("body.bold", !form.config.body.bold)
                      }
                      icon="fas fa-bold"
                      label="Bold"
                    />
                    <ToggleBtn
                      active={form.config.body.italic}
                      onClick={() =>
                        setCfg("body.italic", !form.config.body.italic)
                      }
                      icon="fas fa-italic"
                      label="Italic"
                    />
                  </div>
                </Section>

                <Section title="Image">
                  <Field label="URL">
                    <input
                      className="form-control form-control-sm"
                      value={form.config.image.url}
                      onChange={(e) => setCfg("image.url", e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      maxLength={500}
                    />
                  </Field>
                  <div className="iam-grid-3">
                    <Field label={`Width · ${form.config.image.widthPct}%`}>
                      <input
                        type="range"
                        min={10}
                        max={100}
                        value={form.config.image.widthPct}
                        onChange={(e) =>
                          setCfg("image.widthPct", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                    <Field label={`Radius · ${form.config.image.radius}px`}>
                      <input
                        type="range"
                        min={0}
                        max={48}
                        value={form.config.image.radius}
                        onChange={(e) =>
                          setCfg("image.radius", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                    <Field label="Fit">
                      <Select
                        value={form.config.image.fit}
                        onChange={(e) => setCfg("image.fit", e.target.value)}
                      >
                        <option value="fillWidth">Fit width</option>
                        <option value="cover">Cover</option>
                        <option value="contain">Contain</option>
                      </Select>
                    </Field>
                  </div>
                </Section>

                <Section
                  title="Button"
                  right={
                    <ToggleBtn
                      active={form.config.button.show}
                      onClick={() =>
                        setCfg("button.show", !form.config.button.show)
                      }
                      icon="fas fa-toggle-on"
                      label={form.config.button.show ? "Shown" : "Hidden"}
                    />
                  }
                >
                  {form.config.button.show && (
                    <>
                      <div className="iam-grid-3">
                        <Field label="Text">
                          <input
                            className="form-control form-control-sm"
                            value={form.config.button.text}
                            onChange={(e) =>
                              setCfg("button.text", e.target.value)
                            }
                            maxLength={60}
                          />
                        </Field>
                        <ColorField
                          label="Background"
                          value={form.config.button.color}
                          onChange={(v) => setCfg("button.color", v)}
                        />
                        <ColorField
                          label="Text"
                          value={form.config.button.textColor}
                          onChange={(v) => setCfg("button.textColor", v)}
                        />
                      </div>
                      <div className="iam-grid-2">
                        <Field
                          label={`Text size · ${form.config.button.textSize}px`}
                        >
                          <input
                            type="range"
                            min={10}
                            max={40}
                            value={form.config.button.textSize}
                            onChange={(e) =>
                              setCfg(
                                "button.textSize",
                                parseInt(e.target.value),
                              )
                            }
                            className="form-control-range"
                          />
                        </Field>
                        <Field
                          label={`Radius · ${form.config.button.radius}px`}
                        >
                          <input
                            type="range"
                            min={0}
                            max={40}
                            value={form.config.button.radius}
                            onChange={(e) =>
                              setCfg("button.radius", parseInt(e.target.value))
                            }
                            className="form-control-range"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                </Section>

                <Section title="Action on tap">
                  <div className="iam-grid-3">
                    <Field label="Type">
                      <Select
                        value={form.config.action.type}
                        onChange={(e) => setCfg("action.type", e.target.value)}
                      >
                        <option value="none">None (just dismiss)</option>
                        <option value="screen">In-app screen</option>
                        <option value="url">External URL</option>
                      </Select>
                    </Field>
                    {form.config.action.type !== "none" && (
                      <Field label="Value" span={2}>
                        <input
                          className="form-control form-control-sm"
                          value={form.config.action.value}
                          onChange={(e) =>
                            setCfg("action.value", e.target.value)
                          }
                          placeholder={
                            form.config.action.type === "screen"
                              ? "/home"
                              : "https://example.com/page"
                          }
                          maxLength={500}
                        />
                      </Field>
                    )}
                  </div>
                </Section>

                <Section title="Advanced" collapsed>
                  <div className="iam-grid-2">
                    <Field label={`Card radius · ${form.config.card.radius}px`}>
                      <input
                        type="range"
                        min={0}
                        max={48}
                        value={form.config.card.radius}
                        onChange={(e) =>
                          setCfg("card.radius", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                    <Field label={`Max width · ${form.config.card.maxWidth}px`}>
                      <input
                        type="range"
                        min={200}
                        max={800}
                        value={form.config.card.maxWidth}
                        onChange={(e) =>
                          setCfg("card.maxWidth", parseInt(e.target.value))
                        }
                        className="form-control-range"
                      />
                    </Field>
                  </div>
                  <div className="iam-toggles">
                    <ToggleBtn
                      active={form.config.backdrop.dismissible}
                      onClick={() =>
                        setCfg(
                          "backdrop.dismissible",
                          !form.config.backdrop.dismissible,
                        )
                      }
                      icon="fas fa-hand-pointer"
                      label="Dismiss on backdrop tap"
                    />
                    <ToggleBtn
                      active={form.config.close.show}
                      onClick={() =>
                        setCfg("close.show", !form.config.close.show)
                      }
                      icon="fas fa-times"
                      label="Show close icon"
                    />
                  </div>
                </Section>
              </div>

              {/* preview */}
              <div className="iam-modal__preview">
                <div className="iam-modal__preview-label">
                  <i className="fas fa-eye mr-1" /> Live preview
                </div>
                <div className="iam-phone">
                  <div className="iam-phone__notch" />
                  <div className="iam-phone__screen">
                    <div className="iam-phone__statusbar">
                      <span>9:41</span>
                      <span className="iam-phone__statusbar-icons">
                        <i className="fas fa-signal" />
                        <i className="fas fa-wifi" />
                        <i className="fas fa-battery-three-quarters" />
                      </span>
                    </div>
                    <Preview form={form} />
                  </div>
                </div>
              </div>
            </div>

            <div className="iam-modal__foot">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={save}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-1" /> Saving…
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-1" /> Save message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============== DRAWER =============== */}
      <div
        className={"iam-drawer" + (panelOpen ? " is-open" : "")}
        onClick={() => setPanelOpen(false)}
      >
        <div className="iam-drawer__panel" onClick={(e) => e.stopPropagation()}>
          <div className="iam-drawer__head">
            <span className="iam-drawer__title">
              <i className="fas fa-list mr-2" /> All messages
              <span className="iam-badge ml-2">{items.length}</span>
            </span>
            <button
              type="button"
              className="iam-modal__close"
              onClick={() => setPanelOpen(false)}
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="iam-drawer__search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="iam-drawer__body">
            {filtered.length === 0 ? (
              <div className="iam-panel__empty">
                <i className="fas fa-inbox d-block mb-2" /> No messages
              </div>
            ) : (
              <ul className="iam-cards iam-cards--drawer">
                {filtered.map((it) =>
                  renderRow(it, { closeDrawerOnClick: true, showEdit: false }),
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const styles = `
/* ============ topbar ============ */
.iam-topbar{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:0 0 16px;border-bottom:1px solid #e2e8f0;margin-bottom:16px;flex-wrap:wrap;}
.iam-topbar__info{display:flex;align-items:center;gap:20px;flex-wrap:wrap;}
.iam-topbar__title{font-size:1.05rem;font-weight:700;color:#0f172a;margin:0;}
.iam-topbar__stats{display:flex;gap:14px;font-size:.78rem;color:#64748b;}
.iam-stat strong{color:#0f172a;}
.iam-stat--active strong{color:#10b981;}
.iam-topbar__actions{display:flex;gap:8px;}
.iam-badge{display:inline-block;margin-left:6px;background:#ecfeff;color:#0e7490;font-size:.66rem;font-weight:700;border-radius:10px;padding:1px 7px;}

/* ============ main grid ============ */
.iam-main{display:grid;grid-template-columns:minmax(0,1fr) 380px;gap:20px;align-items:start;}
@media (max-width:991.98px){.iam-main{grid-template-columns:1fr;}}

.iam-panel{background:#fff;border:1px solid #e2e8f0;border-radius:.85rem;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.iam-panel__head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #f1f5f9;}
.iam-panel__title{font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#64748b;font-weight:700;}
.iam-panel__link{background:transparent;border:0;color:#0891b2;font-size:.75rem;font-weight:600;cursor:pointer;padding:0;transition:color .15s ease;}
.iam-panel__link:hover{color:#0e7490;}
.iam-panel__search{position:relative;padding:12px 20px 0;}
.iam-panel__search > i{position:absolute;left:32px;top:50%;transform:translateY(-50%);color:#94a3b8;font-size:.72rem;pointer-events:none;}
.iam-panel__search input{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:8px 34px;font-size:.8rem;background:#f8fafc;outline:none;transition:all .15s ease;color:#0f172a;}
.iam-panel__search input:focus{border-color:#0891b2;background:#fff;box-shadow:0 0 0 3px rgba(8,145,178,.12);}
.iam-panel__search-clear{position:absolute;right:28px;top:50%;transform:translateY(-50%);background:transparent;border:0;color:#94a3b8;font-size:.72rem;padding:4px;cursor:pointer;border-radius:3px;transition:all .15s ease;}
.iam-panel__search-clear:hover{color:#ef4444;background:#fef2f2;}
.iam-panel__empty{text-align:center;padding:32px 16px;color:#94a3b8;font-size:.82rem;}
.iam-empty-cta{display:block;margin:10px auto 0;background:transparent;border:1px solid #0891b2;color:#0891b2;border-radius:20px;padding:4px 16px;font-size:.74rem;font-weight:600;cursor:pointer;transition:all .15s ease;}
.iam-empty-cta:hover{background:#ecfeff;}

/* ============ list ============ */
.iam-cards{
  list-style:none;margin:0;padding:6px 0;
  display:flex;flex-direction:column;
  max-height:560px;overflow-y:auto;
}
.iam-cards--drawer{max-height:none;}

.iam-card-row{
  display:flex;flex-direction:column;gap:10px;
  padding:14px 18px;
  cursor:pointer;
  transition:all .18s ease;
  border:1px solid transparent;
  border-radius:10px;
  margin:2px 8px;
  position:relative;
}
/* divider line between adjacent rows */
.iam-card-row + .iam-card-row::before{
  content:"";
  position:absolute;
  top:-6px;
  left:18px;
  right:18px;
  height:1px;
  background:#f1f5f9;
  transition:opacity .18s ease;
}
.iam-card-row:hover + .iam-card-row::before,
.iam-card-row:hover::before,
.iam-card-row--active::before,
.iam-card-row--active + .iam-card-row::before{
  opacity:0;
}
.iam-card-row:hover{
  background:#f8fafc;
  border-color:#e2e8f0;
  box-shadow:0 1px 3px rgba(15,23,42,.04);
}
.iam-card-row--active{
  background:#f0fdff;
  border-color:#a5f3fc;
  box-shadow:0 2px 8px rgba(8,145,178,.08);
}

.iam-card-row__head{display:flex;align-items:center;gap:10px;}
.iam-card-row__meta{display:flex;align-items:center;gap:5px;font-size:.7rem;color:#64748b;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.iam-dot-sep{opacity:.55;}

/* thumbnail + text */
.iam-card-row__main{display:flex;align-items:flex-start;gap:12px;}
.iam-card-row__thumb{
  flex:0 0 auto;
  width:64px;height:64px;
  border-radius:10px;
  overflow:hidden;
  background:#f1f5f9;
  display:flex;align-items:center;justify-content:center;
  border:1px solid #e2e8f0;
  position:relative;
}
.iam-card-row__thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.iam-card-row__thumb--empty{
  color:#cbd5e1;font-size:1.1rem;
  border-style:dashed;
  background:linear-gradient(135deg,#f8fafc,#f1f5f9);
}
.iam-card-row__text{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;}
.iam-card-row__title{font-size:.88rem;font-weight:700;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.iam-card-row__body{font-size:.76rem;color:#475569;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}

/* always-visible action buttons */
.iam-card-row__actions{display:flex;gap:4px;flex:0 0 auto;}
.iam-icon-btn{
  width:30px;height:30px;display:inline-flex;align-items:center;justify-content:center;
  background:#f1f5f9;border:1px solid #e2e8f0;color:#64748b;font-size:.78rem;
  border-radius:8px;cursor:pointer;transition:all .15s ease;
}
.iam-icon-btn:hover{background:#ecfeff;border-color:#a5f3fc;color:#0891b2;}
.iam-icon-btn--danger{background:#fef2f2;border-color:#fecaca;color:#ef4444;}
.iam-icon-btn--danger:hover{background:#fee2e2;border-color:#fca5a5;color:#dc2626;}

/* ============ status pill ============ */
.iam-status-pill{display:inline-flex;align-items:center;gap:7px;background:#fff;border:1px solid #e2e8f0;color:#64748b;border-radius:20px;padding:4px 5px 4px 9px;font-size:.62rem;font-weight:700;cursor:pointer;transition:all .18s ease;flex:0 0 auto;letter-spacing:.05em;text-transform:uppercase;line-height:1;}
.iam-status-pill:hover:not(:disabled){border-color:#cbd5e1;}
.iam-status-pill:disabled{opacity:.5;cursor:not-allowed;}
.iam-status-pill__dot{width:6px;height:6px;border-radius:50%;background:#cbd5e1;transition:background .18s ease;}
.iam-status-pill__label{color:#64748b;transition:color .18s ease;}
.iam-status-pill__switch{width:24px;height:14px;border-radius:8px;background:#e2e8f0;position:relative;transition:background .18s ease;flex:0 0 auto;}
.iam-status-pill__knob{position:absolute;top:2px;left:2px;width:10px;height:10px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgba(15,23,42,.25);transition:transform .18s ease;}
.iam-status-pill.is-active{background:#ecfdf5;border-color:#a7f3d0;}
.iam-status-pill.is-active .iam-status-pill__dot{background:#10b981;}
.iam-status-pill.is-active .iam-status-pill__label{color:#047857;}
.iam-status-pill.is-active .iam-status-pill__switch{background:#10b981;}
.iam-status-pill.is-active .iam-status-pill__knob{transform:translateX(10px);}

/* ============ preview column ============ */
.iam-main__preview{position:sticky;top:16px;}
@media (max-width:991.98px){.iam-main__preview{position:static;}}
.iam-preview-card{background:#fff;border:1px solid #e2e8f0;border-radius:.85rem;padding:18px;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.iam-preview-card__label{
  display:flex;align-items:center;justify-content:center;gap:6px;
  font-size:.7rem;text-transform:uppercase;letter-spacing:.08em;
  color:#64748b;font-weight:700;text-align:center;margin-bottom:14px;
}
.iam-preview-card__label::before{
  content:"";width:6px;height:6px;border-radius:50%;
  background:#10b981;box-shadow:0 0 0 3px rgba(16,185,129,.15);
}
.iam-preview-card__hint{font-size:.72rem;color:#94a3b8;text-align:center;margin-top:12px;font-style:italic;}

/* ============ phone frame ============ */
.iam-phone{width:100%;max-width:300px;margin:0 auto;background:#0f172a;border-radius:26px;padding:10px 8px 14px;box-shadow:0 10px 28px rgba(15,23,42,.22);position:relative;}
.iam-phone__notch{width:100px;height:16px;background:#0f172a;border-radius:0 0 12px 12px;margin:-10px auto 6px;position:relative;z-index:2;}
.iam-phone__notch::after{content:"";position:absolute;top:4px;left:50%;transform:translateX(-50%);width:40px;height:4px;background:#020617;border-radius:3px;}
.iam-phone__screen{background:linear-gradient(160deg,#f1f5f9,#e2e8f0);border-radius:16px;min-height:420px;overflow:hidden;position:relative;display:flex;flex-direction:column;}
.iam-phone__statusbar{display:flex;align-items:center;justify-content:space-between;font-size:.62rem;color:#475569;font-weight:600;padding:6px 12px;position:relative;z-index:3;}
.iam-phone__statusbar-icons i{margin-left:4px;font-size:.62rem;}

/* ============ preview inside phone ============ */
.iam-preview{flex:1;position:relative;}
.iam-preview__backdrop{position:absolute;inset:0;background:rgba(15,23,42,.45);}
.iam-preview__pos{position:absolute;inset:0;display:flex;padding:12px;z-index:2;}
.iam-preview__pos--center{align-items:center;justify-content:center;}
.iam-preview__pos--top{align-items:flex-start;justify-content:center;}
.iam-preview__pos--bottom{align-items:flex-end;justify-content:center;}
.iam-preview__pos--banner_top{align-items:flex-start;}
.iam-preview__pos--banner_bottom{align-items:flex-end;}
.iam-preview__pos--fullscreen{align-items:stretch;padding:0;}
.iam-preview__pos--fullscreen .iam-preview__card{max-width:none !important;border-radius:0 !important;flex:1;}

.iam-preview__card{position:relative;padding:22px 16px 16px;width:100%;box-shadow:0 8px 24px rgba(15,23,42,.22);}
.iam-preview__close{position:absolute;top:8px;right:10px;font-size:.72rem;opacity:.75;}
.iam-preview__title{word-break:break-word;}
.iam-preview__body{margin-top:10px;word-break:break-word;white-space:pre-wrap;}
.iam-preview__image img{width:100%;display:block;}
.iam-preview__button{margin-top:16px;text-align:center;padding:11px 14px;font-weight:600;}

/* ============ modal ============ */
.iam-modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1050;padding:20px;animation:iamFade .15s ease;}
@keyframes iamFade{from{opacity:0;}to{opacity:1;}}
.iam-modal{background:#fff;border-radius:1rem;max-width:1100px;width:100%;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 24px 60px rgba(15,23,42,.35);animation:iamRise .2s ease;}
@keyframes iamRise{from{transform:translateY(20px);opacity:0;}to{transform:translateY(0);opacity:1;}}
.iam-modal__head{display:flex;align-items:center;justify-content:space-between;padding:16px 22px;border-bottom:1px solid #f1f5f9;}
.iam-modal__title{font-size:1rem;font-weight:700;color:#0f172a;}
.iam-modal__sub{font-size:.75rem;color:#64748b;margin-top:2px;}
.iam-modal__close{background:transparent;border:0;width:32px;height:32px;border-radius:.4rem;color:#94a3b8;font-size:1rem;cursor:pointer;transition:all .15s ease;}
.iam-modal__close:hover:not(:disabled){background:#f1f5f9;color:#0f172a;}
.iam-modal__close:disabled{opacity:.4;cursor:not-allowed;}
.iam-modal__body{display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:0;overflow:hidden;flex:1;min-height:0;}
.iam-modal__form{overflow-y:auto;padding:16px 22px;min-width:0;}
.iam-modal__preview{border-left:1px solid #f1f5f9;background:#f8fafc;padding:20px;overflow-y:auto;display:flex;flex-direction:column;align-items:center;}
.iam-modal__preview-label{font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:#64748b;font-weight:700;text-align:center;margin-bottom:12px;}
.iam-modal__foot{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px;border-top:1px solid #f1f5f9;background:#fbfcfe;border-radius:0 0 1rem 1rem;}

@media (max-width:820px){
  .iam-modal__body{grid-template-columns:1fr;}
  .iam-modal__preview{border-left:0;border-top:1px solid #f1f5f9;}
}

/* ============ drawer ============ */
.iam-drawer{position:fixed;inset:0;background:rgba(15,23,42,.45);opacity:0;pointer-events:none;transition:opacity .2s ease;z-index:1040;}
.iam-drawer.is-open{opacity:1;pointer-events:auto;}
.iam-drawer__panel{position:absolute;top:0;right:0;bottom:0;width:100%;max-width:420px;background:#fff;box-shadow:-10px 0 30px rgba(15,23,42,.22);display:flex;flex-direction:column;transform:translateX(100%);transition:transform .25s ease;}
.iam-drawer.is-open .iam-drawer__panel{transform:translateX(0);}
.iam-drawer__head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #f1f5f9;}
.iam-drawer__title{font-size:.9rem;font-weight:700;color:#0f172a;}
.iam-drawer__search{position:relative;padding:12px 20px;border-bottom:1px solid #f1f5f9;}
.iam-drawer__search > i{position:absolute;left:32px;top:50%;transform:translateY(-50%);color:#94a3b8;font-size:.72rem;pointer-events:none;}
.iam-drawer__search input{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:8px 12px 8px 32px;font-size:.82rem;background:#f8fafc;outline:none;}
.iam-drawer__search input:focus{border-color:#0891b2;background:#fff;}
.iam-drawer__body{overflow-y:auto;flex:1;}

/* ============ sections & fields ============ */
.iam-sec{border-bottom:1px solid #f1f5f9;padding:12px 0;}
.iam-sec:last-child{border-bottom:0;}
.iam-sec__head{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.iam-sec__toggle{background:transparent;border:0;font-size:.78rem;font-weight:700;color:#0f172a;cursor:pointer;padding:0;text-transform:uppercase;letter-spacing:.04em;}
.iam-sec__body{padding-top:12px;}

.iam-field{display:flex;flex-direction:column;gap:4px;min-width:0;}
.iam-field > label{font-size:.72rem;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.03em;margin:0;}
.iam-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;}
.iam-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;}
@media (max-width:520px){.iam-grid-2,.iam-grid-3{grid-template-columns:1fr;}}

.iam-chips{display:flex;flex-wrap:wrap;gap:6px;}
.iam-chip{display:inline-flex;align-items:center;background:#fff;border:1px solid #e2e8f0;color:#475569;border-radius:20px;padding:5px 12px;font-size:.74rem;font-weight:600;cursor:pointer;transition:all .15s ease;}
.iam-chip:hover{border-color:#a5f3fc;color:#0891b2;}
.iam-chip.is-active{background:#0891b2;color:#fff;border-color:#0891b2;}

.iam-color{display:flex;gap:6px;align-items:center;}
.iam-color input[type="color"]{width:36px;height:32px;border:1px solid #e2e8f0;border-radius:.35rem;cursor:pointer;padding:2px;background:#fff;}

.iam-toggles{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}
.iam-toggle{background:#fff;border:1px solid #e2e8f0;color:#475569;border-radius:20px;padding:4px 12px;font-size:.72rem;font-weight:600;cursor:pointer;transition:all .15s ease;}
.iam-toggle.is-active{background:#ecfeff;color:#0e7490;border-color:#a5f3fc;}
`;

export default InAppMessage;
